import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '../../api/client';

function fmt(n: number | string | null | undefined) {
  const num = Number(n) || 0;
  return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return '-';
  return new Date(d).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

interface ReconciliationItemData {
  id: string;
  orderId: string;
  orderAmount: string;
  paidAmount: string;
  offsetAmount: string;
  offsetType: string | null;
  reconciliationStatus: string;
  order: {
    id: string;
    orderNumber: string;
    orderCategory: string;
    orderAmount: string;
    paidAmount: string;
    paymentStatus: string;
    orderStatus: string;
    orderStartDate: string | null;
    serviceType: string | null;
    contactPerson: string | null;
    customer: { id: string; fullName: string | null } | null;
  };
}

interface NettingDetailData {
  id: string;
  batchNo: string;
  batchName: string;
  userId: string | null;
  customerId: string | null;
  mode: string;
  totalNettingAmount: string;
  orderCount: number;
  offsetAmount: string;
  offsetType: string | null;
  settlementStatus: string;
  reconciliationStatus: string;
  approvalStatus: string | null;
  approvedAt: string | null;
  settlementAt: string;
  nettingLogCreatedAt: string;
  operatorName: string;
  isInitiator: boolean;
  reconciliationItems: ReconciliationItemData[];
}

export default function NettingReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['nettingDetail', id],
    queryFn: () => get<NettingDetailData>(`/orders/netting/${id}`),
    enabled: !!id,
  });

  const approveMutation = useMutation({
    mutationFn: () => post<{ success: boolean; message: string }>(`/orders/netting/${id}/approve`),
    onSuccess: (res) => {
      setActionMsg({ type: 'success', msg: res.message || '审核通过' });
      queryClient.invalidateQueries({ queryKey: ['nettingDetail', id] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['orderNetting'] });
      queryClient.invalidateQueries({ queryKey: ['accountingData'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (err: unknown) => {
      setActionMsg({ type: 'error', msg: err instanceof Error ? err.message : '操作失败' });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) => post<{ success: boolean; message: string }>(`/orders/netting/${id}/reject`, { reason }),
    onSuccess: (res) => {
      setActionMsg({ type: 'success', msg: res.message || '已拒绝' });
      setShowRejectForm(false);
      queryClient.invalidateQueries({ queryKey: ['nettingDetail', id] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (err: unknown) => {
      setActionMsg({ type: 'error', msg: err instanceof Error ? err.message : '操作失败' });
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-2 text-gray-400">
          <i className="ri-loader-4-line animate-spin text-xl" />
          <span className="text-sm">加载中…</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-14 h-14 mx-auto flex items-center justify-center bg-rose-50 rounded-2xl">
            <i className="ri-error-warning-line text-rose-400 text-2xl" />
          </div>
          <p className="text-sm text-gray-600">无法加载抵消记录</p>
          <p className="text-xs text-gray-400">{error instanceof Error ? error.message : '记录不存在或无权访问'}</p>
          <button onClick={() => navigate('/')} className="text-sm text-teal-600 hover:text-teal-700 cursor-pointer">
            返回首页
          </button>
        </div>
      </div>
    );
  }

  const isPending = data.approvalStatus === '待审核';
  const isApproved = data.approvalStatus === '已通过';
  const isRejected = data.approvalStatus === '已拒绝';
  const canAct = isPending && !data.isInitiator; // only the customer (non-initiator) can approve/reject

  // 收支方向按「分类名含不含『支出』」判，与服务端 `isExpenseCategory` 同式。
  // 原先是 `=== '收入订单'` / `=== '支出订单'` 的精确等值：订单分类名由用户自建
  // （「支出订单-外采」「外协服务」都合法），对不上这两个字面量的行会**两栏都不进**，
  // 审核页上直接看不见那几笔钱，而它们仍在这一批抵消金额里。
  // 关联不到订单的行（`order` 为空）仍然两栏都不进：方向无从判断，硬塞进收入栏
  // 等于替服务端宣布一个我们不知道的收支方向。
  const linkedItems = data.reconciliationItems.filter(i => Boolean(i.order));
  const expenseItems = linkedItems.filter(i => (i.order.orderCategory ?? '').includes('支出'));
  const incomeItems = linkedItems.filter(i => !(i.order.orderCategory ?? '').includes('支出'));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
            <i className="ri-arrow-left-line text-lg" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-semibold text-gray-900 truncate">订单抵消审核</h1>
            <p className="text-xs text-gray-400 font-mono">{data.batchNo}</p>
          </div>
          {isPending && (
            <span className="px-2.5 py-1 bg-amber-50 text-amber-600 text-xs font-medium rounded-full border border-amber-200">
              待审核
            </span>
          )}
          {isApproved && (
            <span className="px-2.5 py-1 bg-teal-50 text-teal-600 text-xs font-medium rounded-full border border-teal-200">
              已通过
            </span>
          )}
          {isRejected && (
            <span className="px-2.5 py-1 bg-rose-50 text-rose-600 text-xs font-medium rounded-full border border-rose-200">
              已拒绝
            </span>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        {/* Action Message */}
        {actionMsg && (
          <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${
            actionMsg.type === 'success' ? 'bg-teal-50 text-teal-700 border border-teal-200' : 'bg-rose-50 text-rose-700 border border-rose-200'
          }`}>
            <i className={actionMsg.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} />
            {actionMsg.msg}
          </div>
        )}

        {/* Summary Card */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 flex items-center justify-center bg-teal-50 rounded-lg">
              <i className="ri-exchange-dollar-line text-teal-600 text-sm" />
            </div>
            <h2 className="text-sm font-semibold text-gray-900">抵消摘要</h2>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-lg px-3 py-2.5">
              <p className="text-[10px] text-gray-400 mb-0.5">抵消模式</p>
              <p className="text-xs font-medium text-gray-800">{data.mode === 'order' ? '订单抵消' : '金额抵消'}</p>
            </div>
            <div className="bg-gray-50 rounded-lg px-3 py-2.5">
              <p className="text-[10px] text-gray-400 mb-0.5">抵消金额</p>
              <p className="text-sm font-bold text-teal-600">¥{fmt(data.totalNettingAmount)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg px-3 py-2.5">
              <p className="text-[10px] text-gray-400 mb-0.5">发起人</p>
              <p className="text-xs font-medium text-gray-800">{data.operatorName || '-'}</p>
            </div>
            <div className="bg-gray-50 rounded-lg px-3 py-2.5">
              <p className="text-[10px] text-gray-400 mb-0.5">申请时间</p>
              <p className="text-xs font-medium text-gray-800">{fmtDate(data.nettingLogCreatedAt)}</p>
            </div>
          </div>

          {data.approvedAt && (
            <div className="text-xs text-gray-400 pt-1">
              审核时间：{fmtDate(data.approvedAt)}
            </div>
          )}
        </div>

        {/* Order Details */}
        {incomeItems.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
              <i className="ri-arrow-down-circle-line text-teal-500" />
              收入订单（应收款）
            </h3>
            <div className="space-y-2">
              {incomeItems.map(item => (
                <div key={item.id} className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-800">
                      {item.order?.orderNumber || item.orderId.slice(0, 8)}
                    </span>
                    <span className="text-xs font-semibold text-gray-900">¥{fmt(item.order?.orderAmount)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">
                      {item.order?.serviceType || '-'} · {item.order?.orderStartDate?.slice(0, 10) || '-'}
                    </span>
                    <span className="text-[10px] text-teal-600 font-medium">抵消 ¥{fmt(item.offsetAmount)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {expenseItems.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
              <i className="ri-arrow-up-circle-line text-rose-500" />
              支出订单（应付款）
            </h3>
            <div className="space-y-2">
              {expenseItems.map(item => (
                <div key={item.id} className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-800">
                      {item.order?.orderNumber || item.orderId.slice(0, 8)}
                    </span>
                    <span className="text-xs font-semibold text-gray-900">¥{fmt(item.order?.orderAmount)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">
                      {item.order?.serviceType || '-'} · {item.order?.orderStartDate?.slice(0, 10) || '-'}
                    </span>
                    <span className="text-[10px] text-rose-600 font-medium">抵消 ¥{fmt(item.offsetAmount)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reject form */}
        {showRejectForm && canAct && (
          <div className="bg-white rounded-xl border border-rose-100 p-4 space-y-3">
            <h3 className="text-xs font-semibold text-rose-600 flex items-center gap-1.5">
              <i className="ri-close-circle-line" />
              拒绝原因（选填）
            </h3>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="请输入拒绝原因…"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:ring-1 focus:ring-rose-300 resize-none"
              rows={3}
            />
            <div className="flex gap-2">
              <button
                onClick={() => { setShowRejectForm(false); setRejectReason(''); }}
                className="flex-1 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
              >
                取消
              </button>
              <button
                onClick={() => rejectMutation.mutate(rejectReason)}
                disabled={rejectMutation.isPending}
                className="flex-1 py-2 text-sm font-medium text-white bg-rose-500 rounded-lg hover:bg-rose-600 disabled:opacity-50 cursor-pointer transition-colors flex items-center justify-center gap-1.5"
              >
                {rejectMutation.isPending && <i className="ri-loader-4-line animate-spin" />}
                确认拒绝
              </button>
            </div>
          </div>
        )}

        {/* Action buttons */}
        {canAct && !showRejectForm && (
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
            <p className="text-xs text-gray-500 text-center mb-2">
              请仔细核对以上抵消信息，确认无误后点击「同意抵消」
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRejectForm(true)}
                className="flex-1 py-2.5 text-sm font-medium text-rose-600 border border-rose-200 rounded-xl hover:bg-rose-50 cursor-pointer transition-colors"
              >
                拒绝
              </button>
              <button
                onClick={() => approveMutation.mutate()}
                disabled={approveMutation.isPending}
                className="flex-[2] py-2.5 text-sm font-medium text-white bg-teal-500 rounded-xl hover:bg-teal-600 disabled:opacity-50 cursor-pointer transition-colors flex items-center justify-center gap-1.5"
              >
                {approveMutation.isPending && <i className="ri-loader-4-line animate-spin" />}
                同意抵消
              </button>
            </div>
          </div>
        )}

        {/* Non-actionable states */}
        {data.isInitiator && isPending && (
          <div className="bg-amber-50 rounded-xl border border-amber-100 p-4 text-center">
            <p className="text-xs text-amber-700">
              <i className="ri-time-line mr-1" />
              等待对方审核确认中…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
